import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, NavLink } from 'react-router-dom';
import LogoHorizontal from '../assets/images/logo-horizontal.png';
import LogoDarkHorizontal from '../assets/images/logo-dark-horizontal.png';
import SessionService from '../services/SessionService';
import { getUser } from '../services/UserService';
import { getSectors, Sector, getWhatsAppStatus, WhatsAppStatus } from '../services/SectorService';
import { useMenu } from '../contexts/MenuContext';
import { getContacts, getContactsWithFilters } from '../services/ContactService';
import '../styles/CombinedMenu/CombinedMenu.css';
import ChatWebSocketService from '../services/ChatWebSocketService';
import { processAvatarUrl } from '../utils/avatarUtils';
import { mergeContactsList } from '../utils/mergeContactsList';
import { AppLayoutProvider } from '../contexts/AppLayoutContext';
import { useNotification } from '../contexts/NotificationContext';
import { useTheme } from '../contexts/ThemeContext';
import { MessageNotificationSoundControls } from './MessageNotificationSoundControls';
import { getMessageNotifyVolume } from '../utils/messageNotificationSoundSettings';

/** Ícone do botão de som no header: ondas ou mudo (volume 0). */
function MenuHeaderSoundGlyph({ muted }: { muted: boolean }) {
    if (muted) {
        return (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M11 5L6 9H2v6h4l5 4V5z" />
                <line x1="22" y1="9" x2="16" y2="15" />
                <line x1="16" y1="9" x2="22" y2="15" />
            </svg>
        );
    }
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M11 5L6 9H2v6h4l5 4V5z" />
            <path d="M15.54 8.46a5 5 0 010 7.07M19.07 4.93a9 9 0 010 14.14" />
        </svg>
    );
}

interface ProfileUpdateEvent extends CustomEvent {
    detail: {
        avatarUrl: string | null;
        name: string;
        timestamp: number;
    };
}

interface UnreadMessagesState {
    [contactId: number]: boolean;
}

interface ViewedStatus {
    [contactId: number]: boolean;
}

// Objeto para armazenar contatos temporariamente antes de adicionar à lista
type PendingContactsBuffer = {
    [contactId: number]: {
        contact: any;
        timestamp: number;
        timeoutId: NodeJS.Timeout;
    }
};

const PROFILE_UPDATED_EVENT = 'profileUpdated';
const CONTACT_BUFFER_TIMEOUT = 1500; // 1.5 segundos de buffer para contatos

const CombinedMenu: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [sectors, setSectors] = useState<Sector[]>([]);
    const [collapsed, setCollapsed] = useState(false);
    const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);
    const [isLoading, setIsLoading] = useState(false);
    const [name, setName] = useState('');
    const [avatar, setAvatar] = useState<string | null>(null);
    const [selectedSector, setSelectedSector] = useState<string>('');
    const [selectedMenuKey, setSelectedMenuKey] = useState<string>('1');
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [expandedSubmenus, setExpandedSubmenus] = useState<string[]>([]);
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [showNotificationBell, setShowNotificationBell] = useState(false);
    const notificationBellRef = useRef<HTMLDivElement>(null);
    const [showMessageSoundMenu, setShowMessageSoundMenu] = useState(false);
    const messageSoundRef = useRef<HTMLDivElement>(null);
    const [messageSoundVolumePct, setMessageSoundVolumePct] = useState(() => Math.round(getMessageNotifyVolume() * 100));
    const soundHoverCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const cancelSoundHoverClose = useCallback(() => {
        if (soundHoverCloseTimerRef.current != null) {
            clearTimeout(soundHoverCloseTimerRef.current);
            soundHoverCloseTimerRef.current = null;
        }
    }, []);

    useEffect(() => () => cancelSoundHoverClose(), [cancelSoundHoverClose]);

    useEffect(() => {
        if (showMessageSoundMenu) {
            setMessageSoundVolumePct(Math.round(getMessageNotifyVolume() * 100));
        }
    }, [showMessageSoundMenu]);
    const [websocketConnectionStatus, setWebsocketConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
    const [whatsappConnectionStatus, setWhatsappConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
    const [isLoadingWhatsappStatus, setIsLoadingWhatsappStatus] = useState(false);
    const userDropdownRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { drawerVisible, setDrawerVisible, setDrawerType } = useMenu();
    const { resolvedTheme, setTheme } = useTheme();
    const { notifications, addNotification, removeByContactId } = useNotification();
    const navigationInProgressRef = useRef(false);
    const [unreadMessagesState, setUnreadMessagesState] = useState<UnreadMessagesState>(() => {
        // Carregar estado persistido do localStorage
        const saved = localStorage.getItem('@ligchat/unreadMessages');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                return parsed;
            } catch (error) {
            }
        }
        return {};
    });

    // Função para persistir o estado de mensagens não lidas
    const persistUnreadMessages = useCallback((newState: UnreadMessagesState) => {
        try {
            localStorage.setItem('@ligchat/unreadMessages', JSON.stringify(newState));
        } catch (error) {
        }
    }, []);

    // Wrapper para setUnreadMessagesState que também persiste
    const setUnreadMessagesStateWithPersistence = useCallback((newState: UnreadMessagesState | ((prev: UnreadMessagesState) => UnreadMessagesState)) => {
        setUnreadMessagesState(prev => {
            const nextState = typeof newState === 'function' ? newState(prev) : newState;
            persistUnreadMessages(nextState);
            return nextState;
        });
    }, [persistUnreadMessages]);
    const [contacts, setContacts] = useState<any[]>([]);

    // Buffer de contatos pendentes
    const pendingContactsRef = useRef<PendingContactsBuffer>({});
    const contactsRef = useRef<any[]>(contacts);
    contactsRef.current = contacts;

    const getKeyFromPath = useCallback((path: string): string => {
        if (path.includes('/dashboard')) return '1';
        if (path.includes('/chat')) return '2';
        if (path.includes('/schedule')) return '3';
        if (path.includes('/crm')) return '5';
        if (path.includes('/profile')) return '7';
        if (path.includes('/labels')) return '8';
        if (path.includes('/access')) return '9';
        if (path.includes('/sectors')) return '10';
        if (path.includes('/ai-assistant')) return '11';
        return '1';
    }, []);

    const fetchSectors = useCallback(async (token: string) => {
        try {
            const sectors = await getSectors(token);
            setSectors(sectors);

            // Verificar se o setor atualmente selecionado ainda existe
            const currentSectorId = selectedSector ? parseInt(selectedSector) : SessionService.getSectorId();
            if (currentSectorId) {
                const sectorExists = sectors.some(s => s.id === currentSectorId);
                if (!sectorExists) {
                    SessionService.removeSectorId();
                    setSelectedSector('');

                    // Disparar evento para notificar que o setor foi alterado
                    const event = new CustomEvent('sectorChanged', {
                        detail: { sectorId: null }
                    });
                    window.dispatchEvent(event);

                    // Redirecionar para o dashboard quando o setor selecionado não existir mais
                    if (location.pathname !== '/dashboard') {
                        navigate('/dashboard');
                    }
                }
            }
        } catch (error) {
            setSectors([]);
        }
    }, [selectedSector, navigate, location.pathname]);

    useEffect(() => {
        const loadInitialSector = async () => {
            // Obter o setor atual
            const sectorId = SessionService.getSectorId();

            // Se tiver um setor definido, atualizar o estado local
            if (sectorId) {
                setSelectedSector(sectorId.toString());
            } else {
                setSelectedSector('');
            }

            // Carregar os setores disponíveis
            const token = SessionService.getToken();
            if (token) {
                await fetchSectors(token);
            }
        };

        loadInitialSector();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Executar apenas na montagem

    // Adicionar listener para atualização de setores
    useEffect(() => {
        const handleSectorsUpdated = async () => {
            const token = SessionService.getToken();
            if (token) {
                await fetchSectors(token);
            }
        };

        window.addEventListener('sectorsUpdated', handleSectorsUpdated);

        return () => {
            window.removeEventListener('sectorsUpdated', handleSectorsUpdated);
        };
    }, [fetchSectors]);

    useEffect(() => {
        const handleSectorChange = (event: CustomEvent) => {
            const { sectorId } = event.detail;
            if (sectorId) {
                setSelectedSector(sectorId.toString());
            } else {
                setSelectedSector('');
            }
        };

        window.addEventListener('sectorChanged', handleSectorChange as EventListener);
        return () => {
            window.removeEventListener('sectorChanged', handleSectorChange as EventListener);
        };
    }, []);

    // Listener para mudanças de status do WebSocket
    useEffect(() => {
        const handleWebSocketStatusChange = (event: CustomEvent) => {
            const { status } = event.detail;
            setWebsocketConnectionStatus(status);
        };

        // Definir status inicial
        setWebsocketConnectionStatus(ChatWebSocketService.connectionStatus);

        window.addEventListener('websocketStatusChange', handleWebSocketStatusChange as EventListener);
        return () => {
            window.removeEventListener('websocketStatusChange', handleWebSocketStatusChange as EventListener);
        };
    }, []);

    // Função para buscar o status da conexão WhatsApp
    const fetchWhatsAppStatus = useCallback(async (sectorId: string) => {
        if (!sectorId) {
            setWhatsappConnectionStatus('disconnected');
            return;
        }

        try {
            setIsLoadingWhatsappStatus(true);
            const status = await getWhatsAppStatus(Number(sectorId), false);
            setWhatsappConnectionStatus(status.status);
        } catch (error) {
            console.error('[CombinedMenu] Erro ao buscar status do WhatsApp:', error);
            setWhatsappConnectionStatus('disconnected');
        } finally {
            setIsLoadingWhatsappStatus(false);
        }
    }, []);

    // Buscar status do WhatsApp quando o setor muda
    useEffect(() => {
        if (selectedSector) {
            fetchWhatsAppStatus(selectedSector);
        } else {
            setWhatsappConnectionStatus('disconnected');
        }
    }, [selectedSector, fetchWhatsAppStatus]);

    // Listener para mudanças de status do WhatsApp via WebSocket
    useEffect(() => {
        const handleWhatsAppStatusChange = (event: CustomEvent) => {
            const { sectorId, status } = event.detail;

            // Só atualiza se for o setor selecionado
            if (String(sectorId) === selectedSector && status) {
                console.log('[CombinedMenu] WhatsApp status change via WebSocket:', status);
                setWhatsappConnectionStatus(status as 'connecting' | 'connected' | 'disconnected' | 'error');
            }
        };

        window.addEventListener('whatsappStatusChange', handleWhatsAppStatusChange as EventListener);

        return () => {
            window.removeEventListener('whatsappStatusChange', handleWhatsAppStatusChange as EventListener);
        };
    }, [selectedSector]);

    // Listener para eventos de contato visualizado (disparado pelos componentes Chat)
    useEffect(() => {
        const handleContactViewed = (event: CustomEvent) => {
            const { contactId } = event.detail;
            if (contactId) {
                console.log('[CombinedMenu] Contact viewed event received for contact:', contactId);
                setUnreadMessagesStateWithPersistence(prev => {
                    const newState = { ...prev };
                    delete newState[contactId];
                    return newState;
                });
            }
        };

        window.addEventListener('contactViewed', handleContactViewed as EventListener);
        return () => {
            window.removeEventListener('contactViewed', handleContactViewed as EventListener);
        };
    }, [setUnreadMessagesStateWithPersistence]);

    useEffect(() => {
        const checkAuthentication = () => {
            const tokenFromSession = SessionService.getToken();
            if (!tokenFromSession) {
                navigate('/');
                return false;
            }

            if (SessionService.isTokenExpired(tokenFromSession)) {
                SessionService.clearSession();
                navigate('/');
                return false;
            }

            return true;
        };

        checkAuthentication();
    }, [navigate]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
            if (window.innerWidth >= 768) {
                setDrawerVisible(false);
            }
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, [setDrawerVisible]);

    useEffect(() => {
        const paths = ['/access', '/sectors', '/ai-assistant'];
        if (paths.includes(location.pathname)) {
            setExpandedSubmenus((prev) => (prev.includes('6') ? prev : [...prev, '6']));
        }
    }, [location.pathname]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
                setShowUserDropdown(false);
            }
            if (notificationBellRef.current && !notificationBellRef.current.contains(event.target as Node)) {
                setShowNotificationBell(false);
            }
            if (messageSoundRef.current && !messageSoundRef.current.contains(event.target as Node)) {
                cancelSoundHoverClose();
                setShowMessageSoundMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [cancelSoundHoverClose]);

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const token = SessionService.getToken();
                const decodedToken = token ? SessionService.decodeToken(token) : null;
                const userId = decodedToken ? decodedToken.userId : null;

                if (!userId) return;

                // Verificar se é admin ou superadmin (ambos veem Configurações)
                const isAdminOrSuperAdmin = SessionService.isCurrentUserAdmin() || SessionService.isSuperAdmin();
                setIsAdmin(isAdminOrSuperAdmin);

                const response = await fetch(`${process.env.REACT_APP_API_URL}/users/profile`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept': 'application/json'
                    }
                });

                if (response.ok) {
                    const userData = await response.json();
                    if (userData?.data) {
                        setName(userData.data.name || '');
                        if (userData.data.avatarUrl) {
                            const processedUrl = processAvatarUrl(userData.data.avatarUrl);
                            setAvatar(processedUrl);
                        } else {
                            setAvatar(null);
                        }
                    }
                }
            } catch (error) {
            }
        };

        fetchUserData();
    }, []);

    useEffect(() => {
        const handleProfileUpdate = (event: Event) => {
            const profileEvent = event as ProfileUpdateEvent;

            const newAvatarUrl = profileEvent.detail.avatarUrl;
            const newName = profileEvent.detail.name;

            if (newName) {
                setName(newName);
            }

            if (newAvatarUrl) {
                const processedUrl = processAvatarUrl(newAvatarUrl);
                setAvatar(processedUrl);
            } else {
                setAvatar(null);
            }
        };

        window.addEventListener(PROFILE_UPDATED_EVENT, handleProfileUpdate);

        return () => {
            window.removeEventListener(PROFILE_UPDATED_EVENT, handleProfileUpdate);
        };
    }, []);

    useEffect(() => {
        if (selectedSector) {
            const sectorId = parseInt(selectedSector);
            const sector = sectors.find(s => s.id === sectorId);

            if (sector) {
                // Atualizar o setor no SessionService sem disparar evento
                SessionService.setSectorId(sectorId, sector.isOfficial);
            }
        }
    }, [selectedSector, sectors]);

    // Função para buscar contatos do setor selecionado (apenas ativos por padrão)
    const fetchContacts = useCallback(async (sectorId: string) => {
        if (!sectorId) return;
        try {
            // Usar a nova API que busca apenas contatos ativos (não arquivados)
            const response = await getContactsWithFilters(Number(sectorId), false, false);
            if (response && response.data) {
                // Usar a ordem original da API sem reordenar
                setContacts(response.data);
                // Reconstruir mapa de não lidos a partir do backend (autoridade)
                const nextUnread: UnreadMessagesState = response.data.reduce((acc: any, contact: any) => {
                    acc[contact.id] = !contact.isViewed;
                    return acc;
                }, {} as UnreadMessagesState);
                setUnreadMessagesStateWithPersistence(nextUnread);
            } else {
                setContacts([]);
            }
        } catch (error) {
            // Fallback para a API antiga se a nova falhar
            try {
                const fallbackResponse = await getContacts(Number(sectorId));
                if (fallbackResponse && fallbackResponse.data) {
                    // Filtrar contatos arquivados manualmente se necessário
                    const activeContacts = fallbackResponse.data.filter((contact: any) => !contact.archived);
                    setContacts(activeContacts);
                    const nextUnread: UnreadMessagesState = activeContacts.reduce((acc: any, contact: any) => {
                        acc[contact.id] = !contact.isViewed;
                        return acc;
                    }, {} as UnreadMessagesState);
                    setUnreadMessagesStateWithPersistence(nextUnread);
                } else {
                    setContacts([]);
                }
            } catch (fallbackError) {
                setContacts([]);
            }
        }
    }, [setUnreadMessagesStateWithPersistence]);

    // Buscar contatos sempre que o setor mudar
    useEffect(() => {
        if (selectedSector) {
            fetchContacts(selectedSector);
        }
    }, [selectedSector, fetchContacts]);

    // Função para adicionar um contato após o período de buffer
    const addOrUpdateBufferedContact = useCallback((contactData: any) => {
        const contactId = contactData.id;

        // Se já existe um timeout para este contato, limpe-o
        if (pendingContactsRef.current[contactId]?.timeoutId) {
            clearTimeout(pendingContactsRef.current[contactId].timeoutId);
        }

        // Atualizar o contato no buffer
        const timeoutId = setTimeout(() => {
            // Quando o timeout acontecer, adicione/atualize o contato na lista
            setContacts(prev => {
                const safePrev = Array.isArray(prev) ? prev : [];
                const exists = safePrev.some(c => c.id === contactId);
                const finalContactData = pendingContactsRef.current[contactId].contact;

                delete pendingContactsRef.current[contactId];

                if (exists) {
                    return safePrev.map(c => c.id === contactId ? { ...c, ...finalContactData } : c);
                } else {
                    return [...safePrev, finalContactData];
                }
            });
        }, CONTACT_BUFFER_TIMEOUT);

        // Armazenar ou atualizar no buffer
        pendingContactsRef.current[contactId] = {
            contact: contactData,
            timestamp: Date.now(),
            timeoutId: timeoutId
        };
    }, []);

    // WebSocket para notificações globais de mensagens não lidas
    useEffect(() => {
        const token = SessionService.getToken?.() || '';
        if (!token || !selectedSector) return;

        // Define uma flag global para indicar que CombinedMenu está gerenciando o websocket principal
        (window as any).combinedMenuWebSocketActive = true;

        const handleWebSocketMessage = (msg: any) => {
            const msgType = msg.type || 'unknown';

            if (msgType === 'unread_status') {
                const unread = msg.payload.unreadStatus || {};
                const normalized = Object.fromEntries(
                    Object.entries(unread).map(([id, value]) => [Number(id), !value])
                ) as UnreadMessagesState;
                // Substituir completamente pelo status do backend
                setUnreadMessagesStateWithPersistence(normalized);
                // Não precisamos de fetchContacts aqui, apenas atualizamos status
            } else if (msgType === 'message') {
                const contactId = msg.payload.contactID;

                // Determinar se é uma mensagem enviada pelo usuário ou recebida
                const isSentByUser = msg.payload.isSent === true;


                if (!isSentByUser) {
                    setUnreadMessagesStateWithPersistence(prev => ({
                        ...prev,
                        [contactId]: true
                    }));
                }

                // Buscar contatos para atualização completa de dados
                fetchContacts(selectedSector);
            } else if (msgType === 'contact') {

                // Formatar o nome padrão baseado no número se o nome não estiver presente
                const contactData = { ...msg.payload };
                if (!contactData.name || contactData.name.trim() === '') {
                    // Formatar número como nome: +XX YY ZZZZ-ZZZZ -> "Contato +XX YY ZZZZ-ZZZZ"
                    const formattedNumber = contactData.number || '';
                    contactData.name = `Contato ${formattedNumber}`;
                }

                // Verificar se temos todos os campos necessários
                if (!contactData.id || !contactData.number) {
                    return; // Não processar contato incompleto
                }

                // Não processar eventos de contato individuais, aguardar contacts_list para atualização completa
                // fetchContacts(selectedSector);
            } else if (msgType === 'contacts_list') {

                if (msg.payload?.contacts && Array.isArray(msg.payload.contacts)) {
                    setContacts(prev => mergeContactsList(prev || [], msg.payload.contacts));
                    // Reconstruir mapa de não lidos com base no backend
                    const nextUnread: UnreadMessagesState = msg.payload.contacts.reduce((acc: any, contact: any) => {
                        acc[contact.id] = !contact.isViewed;
                        return acc;
                    }, {} as UnreadMessagesState);
                    setUnreadMessagesStateWithPersistence(nextUnread);
                }
            } else if (msgType === 'contact_responsible_update') {
                const payload = msg.payload;
                const currentUserId = SessionService.getCurrentUserId();
                if (currentUserId && payload?.responsibleId === currentUserId) {
                    const contactId = Number(payload.contactId);
                    const contact = contactsRef.current.find((c: any) => c.id === contactId);
                    const contactName = contact?.name || payload.contactName || 'Contato';
                    addNotification('transfer', contactId, contactName);
                }
            }
        };

        ChatWebSocketService.connect(token, handleWebSocketMessage, Number(selectedSector));

        ChatWebSocketService.addMessageHandler(handleWebSocketMessage);

        return () => {
            // Limpar todos os timeouts ao desmontar
            Object.values(pendingContactsRef.current).forEach(item => {
                clearTimeout(item.timeoutId);
            });
            pendingContactsRef.current = {};

            ChatWebSocketService.removeMessageHandler(handleWebSocketMessage);

            // Não desconectar o WebSocket aqui - deixar que outros componentes gerenciem
            // ChatWebSocketService.disconnect();
            (window as any).combinedMenuWebSocketActive = false;
        };
    }, [selectedSector, fetchContacts, addNotification]);

    useEffect(() => {
        const handleWhatsAppConnected = (event: CustomEvent) => {
            const { sectorId: connectedSectorId } = event.detail;

            // Atualizar status do WhatsApp para conectado
            if (connectedSectorId && String(connectedSectorId) === selectedSector) {
                setWhatsappConnectionStatus('connected');
            }

            // Aguardar um pouco para garantir que o backend processou a conexão
            setTimeout(() => {
                // Desconectar e reconectar o WebSocket
                ChatWebSocketService.disconnect();
                const token = SessionService.getToken?.() || '';
                const currentSectorId = selectedSector || SessionService.getSectorId();

                if (token && currentSectorId) {
                    ChatWebSocketService.connect(token, () => { }, Number(currentSectorId))
                        .then(() => {
                        })
                        .catch((error) => {
                        });
                }

                // Recarregar contatos
                if (currentSectorId) {
                    fetchContacts(String(currentSectorId));
                }

                // Atualizar status do WhatsApp via API
                if (currentSectorId) {
                    fetchWhatsAppStatus(String(currentSectorId));
                }
            }, 1000); // Aguardar 1 segundo
        };

        const handleWhatsAppDisconnected = (event: CustomEvent) => {
            const { sectorId: disconnectedSectorId } = event.detail || {};

            // Atualizar status do WhatsApp para desconectado
            if (!disconnectedSectorId || String(disconnectedSectorId) === selectedSector) {
                setWhatsappConnectionStatus('disconnected');
            }
        };

        const handleReloadActiveContacts = (event: CustomEvent) => {
            const { sectorId } = event.detail;
            if (sectorId && sectorId === selectedSector) {
                // Recarregar contatos ativos
                fetchContacts(selectedSector);
            }
        };

        window.addEventListener('whatsappConnected', handleWhatsAppConnected as EventListener);
        window.addEventListener('whatsappDisconnected', handleWhatsAppDisconnected as EventListener);
        window.addEventListener('reloadActiveContacts', handleReloadActiveContacts as EventListener);

        return () => {
            window.removeEventListener('whatsappConnected', handleWhatsAppConnected as EventListener);
            window.removeEventListener('whatsappDisconnected', handleWhatsAppDisconnected as EventListener);
            window.removeEventListener('reloadActiveContacts', handleReloadActiveContacts as EventListener);
        };
    }, [selectedSector, fetchContacts, fetchWhatsAppStatus]);

    const handleSectorChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;

        try {
            setIsLoading(true);

            if (value) {
                const sectorId = parseInt(value);
                const sector = sectors.find(s => s.id === sectorId);

                if (sector && String(sectorId) !== selectedSector) {
                    await SessionService.setSectorId(sectorId, sector.isOfficial);
                    setSelectedSector(value);
                    window.dispatchEvent(new CustomEvent('sectorChanged', { detail: { sectorId } }));
                }
            } else {
                await SessionService.removeSectorId();
                setSelectedSector('');
                window.dispatchEvent(new CustomEvent('sectorChanged', { detail: { sectorId: null } }));
            }
        } catch (error) {
        } finally {
            setIsLoading(false);
        }
    };

    const toggleSidebar = () => {
        setCollapsed(!collapsed);
    };

    const toggleMobileMenu = () => {
        setDrawerVisible(!drawerVisible);
    };

    const toggleUserDropdown = () => {
        cancelSoundHoverClose();
        setShowMessageSoundMenu(false);
        setShowUserDropdown(!showUserDropdown);
    };

    const toggleSubmenu = (key: string) => {
        if (expandedSubmenus.includes(key)) {
            setExpandedSubmenus(expandedSubmenus.filter(item => item !== key));
        } else {
            setExpandedSubmenus([...expandedSubmenus, key]);
        }
    };

    const handleLogout = () => {
        // Limpar estado persistido de mensagens não lidas
        localStorage.removeItem('@ligchat/unreadMessages');
        SessionService.clearSession();
        navigate('/');
    };

    const getPathFromKey = (key: string): string => {
        switch (key) {
            case '1': return '/dashboard';
            case '2': return '/chat';
            case '3': return '/schedule';
            case '5': return '/crm';
            case '7': return '/profile';
            case '8': return '/labels';
            case '9': return '/access';
            case '10': return '/sectors';
            case '11': return '/ai-assistant';
            default: return '/dashboard';
        }
    };

    const handleSubmenuClick = (parentKey: string, childKey: string) => {
        navigationInProgressRef.current = true;
        setSelectedMenuKey(childKey);
        localStorage.setItem('selectedMenuKey', childKey);

        const path = getPathFromKey(childKey);
        navigate(path);

        if (isMobile) {
            setDrawerVisible(false);
        }
    };

    // Sincronizar selectedMenuKey com a rota atual
    useEffect(() => {
        setSelectedMenuKey(getKeyFromPath(location.pathname));
    }, [location.pathname, getKeyFromPath]);

    // Função para renderizar indicador de status de conexão WhatsApp
    const renderConnectionStatusIndicator = () => {
        // Agora usamos o status da conexão WhatsApp ao invés do WebSocket
        const getStatusColor = () => {
            if (isLoadingWhatsappStatus) {
                return '#f59e0b'; // Amarelo - carregando
            }

            switch (whatsappConnectionStatus) {
                case 'connected':
                    return '#0C85BA'; // Azul - conectado
                case 'connecting':
                    return '#f59e0b'; // Amarelo - conectando
                case 'error':
                    return '#ef4444'; // Vermelho - erro
                case 'disconnected':
                default:
                    return '#ef4444'; // Vermelho - desconectado
            }
        };

        const getStatusTitle = () => {
            if (!selectedSector) {
                return 'Selecione um setor para ver o status do WhatsApp';
            }

            if (isLoadingWhatsappStatus) {
                return 'Verificando conexão do WhatsApp...';
            }

            switch (whatsappConnectionStatus) {
                case 'connected':
                    return 'WhatsApp conectado ✓';
                case 'connecting':
                    return 'Conectando ao WhatsApp...';
                case 'error':
                    return 'Erro na conexão do WhatsApp ⚠';
                case 'disconnected':
                default:
                    return 'WhatsApp desconectado ✗';
            }
        };

        const isAnimating = isLoadingWhatsappStatus || whatsappConnectionStatus === 'connecting';

        return (
            <div
                className="connection-status-indicator"
                title={getStatusTitle()}
                style={{
                    backgroundColor: getStatusColor(),
                    animation: isAnimating ? 'pulse 2s infinite' : 'none',
                    cursor: 'help'
                }}
            />
        );
    };

    const renderMenuItems = () => {
        return (
            <ul className="menu-items">
                <li className={`menu-item ${selectedMenuKey === '1' ? 'active' : ''}`}>
                    <NavLink to="/dashboard" className="menu-item-content" onClick={() => { setSelectedMenuKey('1'); if (isMobile) setDrawerVisible(false); }}>
                        <span className="menu-icon icon-dashboard"></span>
                        <span className="menu-label">Dashboard</span>
                        {collapsed && <div className="menu-tooltip">Dashboard</div>}
                    </NavLink>
                </li>
                <li className={`menu-item ${selectedMenuKey === '2' ? 'active' : ''}`}>
                    <NavLink to="/chat" className="menu-item-content" onClick={() => { setSelectedMenuKey('2'); if (selectedSector) fetchContacts(selectedSector); if (isMobile) setDrawerVisible(false); }}>
                        <span className="menu-icon icon-message"></span>
                        <span className="menu-label">Chat</span>
                        {Object.values(unreadMessagesState).some(hasUnread => hasUnread) && (
                            <div className="notification-dot"></div>
                        )}
                        {collapsed && <div className="menu-tooltip">Chat</div>}
                    </NavLink>
                </li>
                <li className={`menu-item ${selectedMenuKey === '3' ? 'active' : ''}`}>
                    <NavLink to="/schedule" className="menu-item-content" onClick={() => { setSelectedMenuKey('3'); if (isMobile) setDrawerVisible(false); }}>
                        <span className="menu-icon icon-schedule"></span>
                        <span className="menu-label">Agendamento</span>
                        {collapsed && <div className="menu-tooltip">Agendamento</div>}
                    </NavLink>
                </li>
                <li className={`menu-item ${selectedMenuKey === '5' ? 'active' : ''}`}>
                    <NavLink to="/crm" className="menu-item-content" onClick={() => { setSelectedMenuKey('5'); if (isMobile) setDrawerVisible(false); }}>
                        <span className="menu-icon icon-crm"></span>
                        <span className="menu-label">CRM</span>
                        {collapsed && <div className="menu-tooltip">CRM</div>}
                    </NavLink>
                </li>
                <li className={`menu-item ${selectedMenuKey === '8' ? 'active' : ''}`}>
                    <NavLink to="/labels" className="menu-item-content" onClick={() => { setSelectedMenuKey('8'); if (isMobile) setDrawerVisible(false); }}>
                        <span className="menu-icon icon-tag"></span>
                        <span className="menu-label">Etiquetas</span>
                        {collapsed && <div className="menu-tooltip">Etiquetas</div>}
                    </NavLink>
                </li>
                {isAdmin && (
                    <li className={`menu-item ${expandedSubmenus.includes('6') ? 'expanded' : ''}`}>
                        <div className="menu-item-content" onClick={() => toggleSubmenu('6')}>
                            <span className="menu-icon icon-settings"></span>
                            <span className="menu-label">Configurações</span>
                            <span className="menu-submenu-arrow">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
                                    <path fill="currentColor" d="M7 10l5 5 5-5z" />
                                </svg>
                            </span>
                            {collapsed && <div className="menu-tooltip">Configurações</div>}
                        </div>
                        <ul className="menu-submenu">
                            <li className={`menu-submenu-item ${selectedMenuKey === '9' ? 'active' : ''}`}>
                                <NavLink to="/access" className="menu-submenu-link" onClick={() => { handleSubmenuClick('6', '9'); }}>
                                    <span className="submenu-dot"></span>
                                    <span className="submenu-label">Acessos</span>
                                </NavLink>
                            </li>
                            <li className={`menu-submenu-item ${selectedMenuKey === '10' ? 'active' : ''}`}>
                                <NavLink to="/sectors" className="menu-submenu-link" onClick={() => { handleSubmenuClick('6', '10'); }}>
                                    <span className="submenu-dot"></span>
                                    <span className="submenu-label">Setores</span>
                                </NavLink>
                            </li>
                            <li className={`menu-submenu-item ${selectedMenuKey === '11' ? 'active' : ''}`}>
                                <NavLink to="/ai-assistant" className="menu-submenu-link" onClick={() => { handleSubmenuClick('6', '11'); }}>
                                    <span className="submenu-dot"></span>
                                    <span className="submenu-label">Assistente IA</span>
                                </NavLink>
                            </li>
                        </ul>
                    </li>
                )}
            </ul>
        );
    };

    const layoutContextValue = {
        contacts,
        setContacts,
        unreadMessages: unreadMessagesState,
        setUnreadMessages: setUnreadMessagesStateWithPersistence
    };

    return (
        <AppLayoutProvider value={layoutContextValue}>
        <div className="menu-layout">
            <header className="menu-header">
                <div className="menu-header-left">
                    <button className="menu-toggle-button" onClick={isMobile ? toggleMobileMenu : toggleSidebar}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                            <path fill="currentColor" d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
                        </svg>
                    </button>
                    <img src={resolvedTheme === 'dark' ? LogoDarkHorizontal : LogoHorizontal} alt="LigChat" className="menu-logo" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer', marginLeft: '10px' }} />
                </div>
                <div className="menu-header-right">
                    <div
                        className={`menu-message-sound${showMessageSoundMenu ? ' menu-message-sound--open' : ''}`}
                        ref={messageSoundRef}
                        onMouseEnter={() => {
                            if (!isMobile) {
                                cancelSoundHoverClose();
                                setShowMessageSoundMenu(true);
                                setShowNotificationBell(false);
                            }
                        }}
                        onMouseLeave={() => {
                            if (!isMobile) {
                                cancelSoundHoverClose();
                                soundHoverCloseTimerRef.current = setTimeout(() => {
                                    setShowMessageSoundMenu(false);
                                    soundHoverCloseTimerRef.current = null;
                                }, 220);
                            }
                        }}
                    >
                        <button
                            type="button"
                            className="menu-bell-button menu-sound-button"
                            onClick={() => {
                                if (!isMobile) return;
                                setShowMessageSoundMenu((open) => !open);
                                setShowNotificationBell(false);
                            }}
                            title={messageSoundVolumePct <= 0 ? 'Som mudo — toque para ajustar' : 'Som ao receber mensagens no chat'}
                            aria-expanded={showMessageSoundMenu}
                            aria-label="Som de novas mensagens"
                        >
                            <MenuHeaderSoundGlyph muted={messageSoundVolumePct <= 0} />
                        </button>
                        {showMessageSoundMenu && (
                            <div className="menu-message-sound-dropdown">
                                <MessageNotificationSoundControls variant="menu" onVolumePctChange={setMessageSoundVolumePct} />
                            </div>
                        )}
                    </div>
                    {/* Sino de notificação - à direita do volume */}
                    <div className="menu-notification-bell" ref={notificationBellRef}>
                        <button
                            className="menu-bell-button"
                            onClick={() => {
                                setShowNotificationBell(!showNotificationBell);
                                setShowMessageSoundMenu(false);
                                cancelSoundHoverClose();
                            }}
                            title={notifications.length > 0 ? `${notifications.length} notificação(ões)` : 'Nenhuma notificação'}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                            </svg>
                            {notifications.length > 0 && (
                                <span className="menu-bell-badge">{notifications.length > 9 ? '9+' : notifications.length}</span>
                            )}
                        </button>
                        {showNotificationBell && (
                            <div className="menu-bell-dropdown">
                                <div className="menu-bell-dropdown-header">
                                    <span>Notificações</span>
                                    <span className="menu-bell-dropdown-count">{notifications.length}</span>
                                </div>
                                {notifications.length === 0 ? (
                                    <div className="menu-bell-dropdown-empty">
                                        Nenhuma notificação
                                    </div>
                                ) : (
                                    <div className="menu-bell-dropdown-list">
                                        {notifications.slice(0, 10).map((n) => {
                                            const contact = contacts.find((c: any) => c.id === n.contactId);
                                            const avatarUrl = contact?.avatarUrl;
                                            return (
                                                <div
                                                    key={n.id}
                                                    className="menu-bell-dropdown-item"
                                                    onClick={() => {
                                                        setShowNotificationBell(false);
                                                        setSelectedMenuKey('2');
                                                        removeByContactId(n.contactId);
                                                        if (selectedSector) fetchContacts(selectedSector);
                                                        navigate('/chat');
                                                        if (isMobile) setDrawerVisible(false);
                                                        setTimeout(() => {
                                                            const event = new CustomEvent('selectContact', { detail: { contactId: n.contactId } });
                                                            window.dispatchEvent(event);
                                                        }, 300);
                                                    }}
                                                >
                                                    <div className="menu-bell-contact-avatar">
                                                        {avatarUrl ? (
                                                            <img src={avatarUrl} alt={n.contactName} />
                                                        ) : (
                                                            <span>{(n.contactName || '?').charAt(0).toUpperCase()}</span>
                                                        )}
                                                    </div>
                                                    <div className="menu-bell-contact-info">
                                                        <span className="menu-bell-contact-name">{n.contactName}</span>
                                                        <span className="menu-bell-contact-status">
                                                            {n.type === 'pending' ? 'Aguardando atendimento humano' : 'Transferido para você'}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <button
                        className="menu-theme-toggle"
                        onClick={() => {
                            cancelSoundHoverClose();
                            setShowMessageSoundMenu(false);
                            setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
                        }}
                        title={resolvedTheme === 'dark' ? 'Alternar para modo claro' : 'Alternar para modo escuro'}
                        aria-label={resolvedTheme === 'dark' ? 'Modo claro' : 'Modo escuro'}
                    >
                        {resolvedTheme === 'dark' ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="5"/>
                                <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
                            </svg>
                        ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                            </svg>
                        )}
                    </button>
                    <div className="menu-user-profile" onClick={toggleUserDropdown} ref={userDropdownRef}>
                        {avatar ? (
                            <img
                                src={avatar}
                                alt={name}
                                className="menu-avatar"
                                key={avatar}
                                crossOrigin="anonymous"
                                onLoad={() => {
                                    // Avatar carregado
                                }}
                                onError={(e) => {
                                    console.error('Erro ao carregar avatar no menu:', {
                                        src: avatar,
                                        error: e
                                    });

                                    const target = e.target as HTMLImageElement;
                                    target.onerror = null;

                                    // Se for uma URL do Supabase, tentar recarregar uma vez
                                    if (avatar && !avatar.includes('retry=1')) {
                                        const retryUrl = avatar.includes('?')
                                            ? `${avatar}&retry=1`
                                            : `${avatar}?retry=1`;
                                        target.src = retryUrl;
                                    } else {
                                        setAvatar(null);
                                    }
                                }}
                            />
                        ) : (
                            <div className="menu-avatar-placeholder">
                                {name.charAt(0).toUpperCase()}
                            </div>
                        )}
                        <span className="menu-username">{name}</span>
                        <span className="menu-dropdown-arrow">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
                                <path fill="currentColor" d="M7 10l5 5 5-5z" />
                            </svg>
                        </span>
                        {showUserDropdown && (
                            <div className="menu-user-dropdown">
                                <NavLink to="/profile" className="menu-dropdown-item" onClick={() => { setSelectedMenuKey('7'); setShowUserDropdown(false); if (isMobile) setDrawerVisible(false); }}>
                                    <span className="menu-dropdown-icon icon-user"></span>
                                    <span>Perfil</span>
                                </NavLink>
                                <div className="menu-dropdown-item" onClick={handleLogout}>
                                    <span className="menu-dropdown-icon icon-logout"></span>
                                    <span>Sair</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <div className="menu-content-layout">
                <aside className={`menu-sidebar ${collapsed ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''} ${drawerVisible && isMobile ? 'visible' : ''}`}>
                    <div className="menu-sidebar-content">
                        {isLoading ? (
                            <div className="menu-loading">
                                <div className="menu-spinner"></div>
                            </div>
                        ) : (
                            <>
                                {renderMenuItems()}
                                <div className="menu-sector-selector">
                                    {!collapsed && <div className="menu-version">Versão: 1.2.3</div>}
                                    <div className="sector-selector-container">
                                        <select
                                            value={selectedSector}
                                            onChange={handleSectorChange}
                                            className="menu-select"
                                        >
                                            <option value="">Selecione o setor</option>
                                            {sectors.map((sector) => (
                                                <option key={sector.id} value={sector.id}>
                                                    {sector.name}
                                                </option>
                                            ))}
                                        </select>
                                        {renderConnectionStatusIndicator()}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </aside>

                <main className={`menu-content ${location.pathname.includes('/chat') ? 'menu-content-chat' : ''}`}>
                    <div className={`page-container ${location.pathname.includes('/chat') ? 'page-container-chat' : ''}`}>
                        {children}
                    </div>
                </main>
            </div>

            {isMobile && drawerVisible && (
                <div className="menu-overlay" onClick={toggleMobileMenu}></div>
            )}
        </div>
        </AppLayoutProvider>
    );
};

export default CombinedMenu;
